<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";

import {
  ButtonSeverity,
  TuButton,
  TuInputCheckbox,
  TuInputLabel,
  TuInputSelect,
  TuPopover,
  usePopover,
} from "@tinyurldev/tu-ui-vue";

import { Domain } from "@Modules/urls/Data/Models/DomainModel";
import BulkUploadApi from "@Modules/urls/Api/BulkUploadApi";
import BulkGuide from "@Images/home/bulk-guide-organization.png";
import useTranslation from "@Composables/useTranslation";

//
// Setup
const bulkApi = BulkUploadApi();
const isPopoverVisible = ref(false);
const { referenceRef, popoverRef } = usePopover(isPopoverVisible, {
  placement: "bottom",
});
const { __ } = useTranslation();

//
// Props
interface Props {
  organization: string;
  team: string;
  availableTeams: { slug: string; name: string }[];
  availableDomains: Domain[];
  selectableDomains: { value: string; name: string }[];
}

const props = defineProps<Props>();

//
// Data
const form = reactive({
  domain: "",
  withHeader: true,
});
const isDragging = ref(false);
const bulkStatus = ref<{ success: true; total: number } | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);

//
// Computed
const isLoading = computed(() => bulkApi.loading.value);
const errors = computed(() => bulkApi.errors.value);
const file = computed(() => bulkApi.file.value);

const isSubmitDisabled = computed(() => {
  return !file.value || isLoading.value || !!errors.value.file;
});

//
// Methods
function openFileDialog(): void {
  fileInputRef.value?.click();
}

function processFile(event: Event): void {
  const target = event.target as HTMLInputElement;
  const selectedFile = target.files?.[0];
  if (selectedFile) {
    bulkApi.validateAndSetFile(selectedFile);
  }
}

function handleDragOver(event: DragEvent): void {
  event.preventDefault();
  isDragging.value = true;
}

function handleDragLeave(): void {
  isDragging.value = false;
}

function handleDrop(event: DragEvent): void {
  event.preventDefault();
  isDragging.value = false;

  const droppedFile = event.dataTransfer?.files[0];
  if (droppedFile) {
    bulkApi.validateAndSetFile(droppedFile);
  }
}

async function submitForm(): Promise<void> {
  if (!file.value || !props.organization || !props.team) {
    return;
  }

  const result = await bulkApi.uploadTeamBulkCsv({
    orgID: props.organization,
    teamID: props.team,
    domain: form.domain,
    withHeader: form.withHeader,
  });

  if (result.success) {
    bulkStatus.value = result;
  }
}

function startNewBatch(): void {
  bulkApi.clearFile();
  bulkApi.clearErrors();
  form.withHeader = true;
  bulkStatus.value = null;

  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
}

onMounted(() => {
  const domains = props.availableDomains;
  if (domains && domains.length > 0 && domains[0]) {
    form.domain = domains[0].domain;
  }
});

//
// Exposed Test Vars
defineExpose({ form, file, errors, bulkStatus, isLoading, isSubmitDisabled, submitForm, startNewBatch });
</script>

<template>
  <div
    v-if="bulkStatus"
    class="v2-bulk-form__success _success"
  >
    <div class="v2-bulk-form__success-header">
      <i class="far fa-clock" /> {{ __("create_alias.bulk.success_header") }}
    </div>
    <div class="v2-bulk-form__success-count">
      <span>{{ __("create_alias.bulk.success_total_label") }}</span>
      <strong>{{ bulkStatus.total }}</strong>
    </div>
    <TuButton
      class="_start_new_batch"
      :label="__('create_alias.bulk.success_button')"
      :is-block="true"
      :severity="ButtonSeverity.SECONDARY"
      @click="startNewBatch"
    />
  </div>

  <form
    v-else
    class="v2-bulk-form"
    autocomplete="off"
    @submit.prevent="submitForm"
  >
    <!-- File Upload -->
    <div class="v2-bulk-form__attach-file">
      <div class="v2-bulk-form__attach-file-label">
        <TuInputLabel
          id="file-input"
          :label="__('create_alias.bulk.attach_file_label')"
        />
        <TuPopover
          v-show="isPopoverVisible"
          ref="popoverRef"
          class="v2-bulk-form__popover"
        >
          <p class="v2-bulk-form__popover-title">
            {{ __("create_alias.bulk.guide_description") }}
          </p>
          <img
            class="v2-bulk-form__popover-image"
            :src="BulkGuide"
            alt="Bulk upload guide figure"
          >
          <p class="v2-bulk-form__popover-item">
            <span class="v2-bulk-form__popover-label">{{ __("create_alias.bulk.accepted_file_type_label") }}</span>
            {{ __("create_alias.bulk.accepted_file_type_value") }}
          </p>
          <p class="v2-bulk-form__popover-item">
            <span class="v2-bulk-form__popover-label">{{ __("create_alias.bulk.max_file_size_label") }}</span>
            {{ __("create_alias.bulk.max_file_size_value") }}
          </p>
        </TuPopover>
        <div
          ref="referenceRef"
          class="v2-bulk-form__attach-file-info"
          @mouseenter="isPopoverVisible = true"
          @mouseleave="isPopoverVisible = false"
        >
          <i class="fa fa-info-circle" />
        </div>
      </div>

      <div
        class="v2-bulk-form__dropzone"
        :class="{
          'v2-bulk-form__dropzone--dragging': isDragging,
          'v2-bulk-form__dropzone--error': errors.file,
        }"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
      >
        <input
          ref="fileInputRef"
          type="file"
          class="v2-bulk-form__file-input _attach_file"
          accept=".csv,text/csv"
          :disabled="isLoading"
          @change="processFile"
        >

        <template v-if="file">
          <i class="fa-solid fa-file-csv v2-bulk-form__dropzone-icon" />
          <span class="v2-bulk-form__dropzone-filename">{{ file.name }}</span>
        </template>
        <template v-else>
          <i class="fa-solid fa-cloud-arrow-up v2-bulk-form__dropzone-icon" />
          <span class="v2-bulk-form__dropzone-text">{{ __("create_alias.bulk.drag_and_drop_text") }}</span>
        </template>

        <TuButton
          class="v2-bulk-form__dropzone-btn _upload_btn"
          :label="file ? __('create_alias.bulk.change_button') : __('create_alias.bulk.upload_button')"
          :severity="ButtonSeverity.PRIMARY"
          :is-outlined="true"
          :is-disabled="isLoading"
          type="button"
          @click="openFileDialog"
        />
      </div>

      <p
        v-if="errors.file"
        class="v2-bulk-form__error"
      >
        {{ errors.file }}
      </p>
    </div>

    <!-- Header Row Checkbox -->
    <TuInputCheckbox
      id="header-checkbox"
      v-model="form.withHeader"
      class="v2-bulk-form__header-checkbox _header_checkbox"
      :label="__('create_alias.bulk.header_checkbox_label')"
      :is-disabled="isLoading"
    />

    <!-- Domain Select -->
    <TuInputSelect
      v-model="form.domain"
      class="v2-bulk-form__domain _domain_select"
      :label="{ id: 'domain', label: __('create_alias.bulk.domain_label'), icon: 'fa-solid fa-globe' }"
      :placeholder="__('create_alias.bulk.domain_placeholder')"
      :options="selectableDomains"
      :is-disabled="isLoading"
    />

    <!-- Submit Button -->
    <TuButton
      class="v2-bulk-form__submit _submit_btn"
      :label="__('create_alias.bulk.submit_button')"
      :is-block="true"
      :is-disabled="isSubmitDisabled"
      :severity="ButtonSeverity.SECONDARY"
      :is-loading="isLoading"
      type="submit"
    />
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/position.scss" as position;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.v2-bulk-form {
  &__attach-file {
    margin-bottom: spacing.$xxs;
  }

  &__attach-file-label {
    display: flex;
    align-items: center;
    margin-bottom: spacing.$xxxs;
  }

  &__attach-file-info {
    display: flex;
    align-items: center;
    margin-left: spacing.$xs-5;
    cursor: pointer;
    color: color.$text-secondary;

    &:hover {
      color: color.$primary;
    }
  }

  &__popover {
    z-index: position.$highest-z-index;
    padding: spacing.$xxs;
    max-width: 500px;
  
    font-size: typography.$s-font-size;
  }

  &__popover-title {
    font-weight: typography.$font-weight-semi-bold;
    margin-bottom: spacing.$xxxs;
  }

  &__popover-item {
    margin-bottom: 0;
  }

  &__popover-image {
    margin-bottom: spacing.$xxxs;
  }

  &__popover-label {
    font-weight: typography.$font-weight-semi-bold;
  }

  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: spacing.$s;
    background-color: color.$text-white;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%23DEE2E6FF' stroke-width='1' stroke-dasharray='8' stroke-dashoffset='5' stroke-linecap='square'/%3e%3c/svg%3e");
    border-radius: border.$small-radius;
    position: relative;
    min-height: 120px;

    &--dragging {
      background-color: color.$secondary-light;
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%231EB980FF' stroke-width='2' stroke-dasharray='8' stroke-dashoffset='5' stroke-linecap='square'/%3e%3c/svg%3e");
    }

    &--error {
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%23C31818FF' stroke-width='1' stroke-dasharray='8' stroke-dashoffset='5' stroke-linecap='square'/%3e%3c/svg%3e");
    }
  }

  &__file-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    overflow: hidden;
  }

  &__dropzone-icon {
    font-size: 24px;
    color: color.$text-secondary;
    margin-bottom: spacing.$xxxs;
  }

  &__dropzone-text {
    font-size: typography.$s-font-size;
    color: color.$text-secondary;
    margin-bottom: spacing.$xxs;
  }

  &__dropzone-filename {
    font-size: typography.$s-font-size;
    color: color.$text;
    font-weight: typography.$font-weight-semi-bold;
    margin-bottom: spacing.$xxs;
    word-break: break-all;
    text-align: center;
  }

  &__dropzone-btn {
    min-width: 100px;
  }

  &__error {
    color: color.$alert;
    font-size: typography.$xs-font-size;
    margin-top: spacing.$xxxs;
    margin-bottom: 0;
  }

  &__header-checkbox {
    margin-bottom: spacing.$xxs;
  }

  &__domain {
    margin-bottom: spacing.$xxs;
  }

  &__submit {
    margin-top: spacing.$xxs;
  }

  &__success {
    text-align: center;
    padding: spacing.$m;
  }

  &__success-header {
    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-semi-bold;
    margin-bottom: spacing.$xxs;
  }

  &__success-count {
    margin-bottom: spacing.$m;

    span {
      margin-right: spacing.$xxxs;
    }
  }
}
</style>
